import { useState } from "react";
import { useLocation, useParams, useSearch } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiRequest, parseJsonResponse } from "@/lib/queryClient";
import { smbPackageListResponseSchema } from "@shared/smb-packages";
import { PackageManager } from "@/components/smb/PackageManager";
import { SmbHome } from "@/components/smb/SmbHome";
import { SmbClientPage } from "@/components/smb/SmbClientPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const createdSchema = z.union([z.object({ order: z.object({ id: z.string() }) }), z.object({ id: z.string() })]);
export default function AdminSmbPage() {
  const params = useParams<{ orderId?: string; step?: string; packageId?: string }>();
  const [location, setLocation] = useLocation();
  const search = useSearch();
  const [creating, setCreating] = useState(false);
  const [externalId, setExternalId] = useState("");
  const [selectedPackage, setSelectedPackage] = useState("");
  const client = useQueryClient();
  const isPackages = location.startsWith("/admin/smb/packages");
  const packages = useQuery({
    queryKey: ["/api/admin/smb/packages", 1, 100],
    queryFn: async () => smbPackageListResponseSchema.parse(await parseJsonResponse(await apiRequest("GET", "/api/admin/smb/packages?page=1&pageSize=100"))),
    enabled: !params.orderId && !isPackages && new URLSearchParams(search).get("view") !== "client-intake",
  });
  const create = useMutation({
    mutationFn: async () => createdSchema.parse(await parseJsonResponse(await apiRequest("POST", "/api/admin/smb/orders", {
      ...(externalId.trim() ? { externalId: externalId.trim() } : {}), ...(selectedPackage ? { packageId: selectedPackage } : {}),
    }))),
    onSuccess: data => {
      void client.invalidateQueries({ queryKey: ["/api/admin/smb/orders"] });
      void client.invalidateQueries({ queryKey: ["/api/admin/smb/board"] });
      setCreating(false);
      setLocation(`/admin/smb/orders/${encodeURIComponent("order" in data ? data.order.id : data.id)}`);
    },
  });
  if (isPackages) return <PackageManager key={params.packageId ?? "package-library"} packageId={params.packageId} setLocation={setLocation} />;
  if (params.orderId) return <SmbClientPage key={params.orderId} id={params.orderId} stepParam={params.step} setLocation={setLocation} />;
  return <>
    <SmbHome search={search} setLocation={setLocation} packages={packages.data?.packages ?? []} onCreate={() => { setCreating(true); setExternalId(""); setSelectedPackage(""); create.reset(); }} />
    <Dialog open={creating} onOpenChange={open => { if (!create.isPending) setCreating(open); }}><DialogContent className="max-h-[90dvh] w-[calc(100%-2rem)] overflow-auto sm:max-w-md">
      <DialogTitle>Create test order</DialogTitle><DialogDescription>Creates an SMB test order for safe inspection.</DialogDescription>
      <label className="grid gap-2 text-sm">External ID<Input aria-label="External ID" value={externalId} onChange={event => setExternalId(event.target.value)} maxLength={160} /><span className="text-xs text-muted-foreground">Optional, 8 to 160 characters</span></label>
      <label className="grid gap-2 text-sm">Package<select className="h-10 rounded-md border bg-background px-3" value={selectedPackage} onChange={event => setSelectedPackage(event.target.value)}><option value="">Use default</option>{packages.data?.packages.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
      {create.error && <p role="alert" className="break-words text-sm text-destructive">{create.error.message}</p>}
      <div className="flex justify-end gap-2"><Button variant="outline" disabled={create.isPending} onClick={() => setCreating(false)}>Cancel</Button><Button disabled={create.isPending || (externalId.length > 0 && externalId.trim().length < 8)} onClick={() => create.mutate()}>Create order</Button></div>
    </DialogContent></Dialog>
  </>;
}
